import * as yup from 'yup';
import { t } from 'services/translation';
import { Guest } from './types';
import { Question } from '../guest_questions/types';

// date example: "Dec 23 2021 13:15"
// Output: 'Dec 23, 2021'
export const convertDateFormat = (date: string): string => {
  if (!date.length) {
    return '';
  }
  const formattedDate = date.substring(0, date.length - 5);
  const seperateDate = formattedDate.split('');
  seperateDate.splice(6, 0, ',');
  const newFormattedDate = seperateDate.join('');
  const extraxtedMonth = newFormattedDate.substring(0, 3);
  const newFormattedTranslatedDate = newFormattedDate.replace(
    extraxtedMonth,
    t(extraxtedMonth.toUpperCase())
  );

  return newFormattedTranslatedDate;
};

// date example: "Dec 23 2021 13:15"
// Output: '1:15pm'
export const convertTimeFormat = (time: string): string => {
  if (!time.length) {
    return '';
  }
  const timeOnly = time.substring(time.length - 5);

  const timeString12hr = new Date(`1970-01-01T${timeOnly}Z`).toLocaleTimeString(
    'en-US',
    {
      timeZone: 'UTC',
      hour12: true,
      hour: 'numeric',
      minute: 'numeric',
    }
  );

  const translatedTimeString12hr = timeString12hr
    .toLowerCase()
    .replace(/am/g, t('AM'))
    .replace(/pm/g, t('PM'));

  return `${translatedTimeString12hr}`;
};

// date example: "Dec 23 2021 13:15"
// Output: 'Dec 23, 2021 at 1:15pm'
export const convertDateTimeFormat = (time: string): string => {
  if (!time.length) {
    return '';
  }

  return `${convertDateFormat(time)}at ${convertTimeFormat(time)}`;
};

export const copyToClipboard = (url: string): any => {
  navigator.clipboard.writeText(url);

  if (navigator.share) {
    navigator
      .share({ title: 'Share AnyRoad Additional Guest Page', url })
      .then(() => {
        console.log('Copied');
      })
      .catch(console.error);
  }
};

export const getHashedBookingIdFromPath = (pathname: string): any => {
  if (pathname) {
    //  Extract hashed booking id from path
    const splitPath = pathname.split('/');
    return splitPath[splitPath.length - 1];
  }
  return '';
};

export const sortBookerGuestFirst = (guests: Guest[]): any => {
  if (!guests || !guests.length) return [];

  if (guests[0].attributes.isBooker === true) {
    return guests;
  }

  const sortedGuests = guests.reduce((acc: Guest[], guest: Guest) => {
    if (guest.attributes.isBooker) {
      return [guest, ...acc];
    }
    return [...acc, guest];
  }, []);

  return sortedGuests;
};

export const setFormContraints = (
  fields: any,
  firstName: string,
  lastName: string,
  _form: string
) => {
  if (!fields) {
    return false;
  }
  const constraints: any = {};
  const constraintFirstName =
    _form === 'AddGuest' ? 'firstName' : 'firstNameField';
  const constraintLastName =
    _form === 'AddGuest' ? 'lastName' : 'lastNameField';

  if (firstName && !lastName) {
    constraints[constraintLastName] = {
      presence: { allowEmpty: false, message: 'Required with first name' },
    };
  } else if (!firstName && lastName) {
    constraints[constraintFirstName] = {
      presence: { allowEmpty: false, message: 'Required with last name' },
    };
  }
  if (fields.email) {
    constraints.email = {
      presence: { allowEmpty: true },
      format: {
        pattern: /\S+@\S+\.\S+/,
        message:
          _form === 'AddGuest'
            ? 'Please enter a valid email'
            : 'This is not a valid email',
      },
    };
  }
  return constraints;
};

interface Data {
  [key: string]: string | boolean;
}

export const formatAnswersData = (data: Data): any => {
  if (!data) {
    return { answers: [] };
  }

  const filteredData = Object.entries(data).filter(
    ([key, value]) =>
      !(value === null || value === undefined || value === '') &&
      key !== 'email' &&
      key !== 'waiver'
  );

  const formattedAnswers = filteredData.map((ans) => {
    let finalAnswer;
    const submittedAnswer = ans[1];

    switch (submittedAnswer) {
      case true:
        finalAnswer = 'yes';
        break;
      case false:
        finalAnswer = 'no';
        break;
      default:
        finalAnswer = submittedAnswer;
    }

    const answer = {
      answer: finalAnswer,
      tour_question_id: ans[0],
    };

    return answer;
  });

  return { answers: formattedAnswers };
};

export const formatEmailData = (data: Data): any => {
  // we have validations on form to always have an email.
  return { email: data.email };
};

export const formatValidationSchema = (questions: Question[]) => {
  const validationObject: { [key: string]: any } = {};

  // always validate email field
  validationObject.email = yup.string().email().required();
  validationObject.waiver = yup.bool().oneOf([true]);

  if (questions) {
    questions.forEach((question: Question) => {
      const { id } = question;
      const { inputType, required } = question.attributes;

      // validate checkbox field
      if (inputType === 'waiver' && required) {
        validationObject[id] = yup.bool().oneOf([true]);
      }

      // validate date field
      if (inputType === 'date' && required) {
        validationObject[id] = yup.string().required();
      }
    });
  }
  return yup.object(validationObject).required();
};

interface Answer {
  id: number;
  input_type: string;
  position: number;
  answer: string;
  answers: string[];
  text: string;
}

interface GuestType {
  id: number;
  firstName: string;
  lastName: string;
  fullName: string;
  bookingId: number;
  guestId: string;
  status: string;
  ticketType?: string;
  ticketId?: string;
  url: string | undefined;
  isMinor: boolean;
  isBooker: boolean;
  hasVisitedExperienceLink: boolean;
  questionsAnswers: Answer[];
}

interface DefaultAnswer {
  [key: string]: string;
}

export const formatDefaultGuestAnswers = (guest: GuestType) => {
  if (
    !guest ||
    !guest.questionsAnswers ||
    guest.questionsAnswers.length === 0
  ) {
    return {};
  }

  const defaultAnswers: DefaultAnswer = guest.questionsAnswers
    .filter((answer) => answer.input_type === 'text')
    .reduce((acc, { id, answers: [firstAnswer] }) => {
      acc[id] = firstAnswer;
      return acc;
    }, {} as DefaultAnswer);

  return defaultAnswers;
};

export const formatTextAnswers = (answerData: Data) => {
  const NON_QUESTION_KEYS = [
    'firstname',
    'lastname',
    'email',
    'phone',
    'waiver',
  ];

  if (!answerData) {
    return { answers: [] };
  }

  const formattedAnswers = Object.entries(answerData)
    .filter(([key]) => !NON_QUESTION_KEYS.includes(key))
    .map(([questionId, answer]) => ({
      tour_question_id: questionId,
      answer,
    }));

  return { answers: formattedAnswers };
};
